import React from "react";
import { useAuth } from "../../app/providers/AuthProvider";
import { FK } from "../../capabilities/featureKeys";
import AiActivityPanel, { activityExplanation } from "./AiActivityPanel";
import { AiLoadError, useAiRequestGuard, withAiAgentScope } from "./aiAgentRequests";
import { Activity, ArrowLeft, Info, RefreshCcw } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  getAiAgentUsage,
  getAiAgentQuotas,
  getControlledAnswerSendLogs,
} from "./api/aiAgentApi";
import { readAiAgentDraft, writeAiAgentDraft } from "./aiAgentDraftStorage";

const AI_USAGE_TAB = "ai-usage";
const CONTROLLED_SENDS_TAB = "controlled-sends";

const STATUS_OPTIONS = ["All", "Started", "Completed", "Denied", "Failed"];
const SOURCE_OPTIONS = ["All", "draft", "auto-reply", "test-console", "embedding", "fallback-dry-run", "lead-qualification-dry-run", "lead-qual-planner-shadow", "lead-qual-semantic-shadow"];
const OPERATIONS_TAB = "operations";
const SOURCE_LABELS = { All: "All activity", draft: "Draft suggestions", "auto-reply": "Reply processing", "test-console": "Answer tests", embedding: "Knowledge processing", "fallback-dry-run": "Fallback previews", "lead-qualification-dry-run": "Lead collection previews", "lead-qual-planner-shadow": "Planner evaluation", "lead-qual-semantic-shadow": "Answer interpretation evaluation" };
const PURPOSE_LABELS = { ControlledAnswer: "Configured reply", ResumeLeadQuestion: "Return to question" };
const money = value => value == null ? "Not recorded" : `$${Number(value).toFixed(6)} USD`;
const validDate = value => /^\d{4}-\d{2}-\d{2}$/.test(value || "") && Number.isFinite(Date.parse(`${value}T00:00:00Z`));
const CONTROLLED_STATUS_OPTIONS = [
  "All",
  "Pending",
  "Attempting",
  "Succeeded",
  "Failed",
  "Skipped",
];
const CONTROLLED_PURPOSE_OPTIONS = [
  "All",
  "ControlledAnswer",
  "ResumeLeadQuestion",
];

const USAGE_FILTER_DRAFT_KEY = "usage-filters";
const CONTROLLED_FILTER_DRAFT_KEY = "controlled-answer-send-filters";

const STATUS_BADGE_CLASSES = {
  Pending: "bg-slate-100 text-slate-700",
  Attempting: "bg-amber-100 text-amber-800",
  Succeeded: "bg-emerald-100 text-emerald-800",
  Failed: "bg-rose-100 text-rose-700",
  Skipped: "bg-slate-100 text-slate-600",
};

function AiAgentUsagePage({scope}) {
  const { can, hasAllAccess } = useAuth() || {};
  const canOperate = Boolean(hasAllAccess) || Boolean(can?.(FK.AI_AUTO_REPLY_MANAGE));
  const canViewSettings = Boolean(hasAllAccess) || Boolean(can?.(FK.AI_AGENT_SETTINGS_VIEW));
  const [activeTab, setActiveTab] = React.useState(AI_USAGE_TAB);

  const restoredUsageFilters = React.useMemo(
    () => readAiAgentDraft(USAGE_FILTER_DRAFT_KEY, scope) || {},
    [scope]
  );
  const restoredControlledFilters = React.useMemo(
    () => readAiAgentDraft(CONTROLLED_FILTER_DRAFT_KEY, scope) || {},
    [scope]
  );

  const [page, setPage] = React.useState(Number(restoredUsageFilters.page) || 1);
  const [pageSize] = React.useState(20);
  const [status, setStatus] = React.useState(
    STATUS_OPTIONS.includes(restoredUsageFilters.status)
      ? restoredUsageFilters.status
      : "All"
  );
  const [source, setSource] = React.useState(
    typeof restoredUsageFilters.source === "string" && restoredUsageFilters.source.length <= 32 ? restoredUsageFilters.source : "All"
  );
  const [loading, setLoading] = React.useState(true);
  const [items, setItems] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [fromDate, setFromDate] = React.useState(validDate(restoredUsageFilters.fromDate) ? restoredUsageFilters.fromDate : new Date(Date.now() - 29 * 86400000).toISOString().slice(0,10));
  const [toDate, setToDate] = React.useState(validDate(restoredUsageFilters.toDate) ? restoredUsageFilters.toDate : new Date().toISOString().slice(0,10));
  const [loadError, setLoadError] = React.useState("");
  const [summary, setSummary] = React.useState(null);
  const [sources, setSources] = React.useState(SOURCE_OPTIONS);
  const [quota, setQuota] = React.useState(null);
  const [quotaError, setQuotaError] = React.useState("");
  const [quotaLoading, setQuotaLoading] = React.useState(false);
  const range = React.useMemo(() => ({
    ...(fromDate ? {fromUtc: `${fromDate}T00:00:00.000Z`} : {}),
    ...(toDate ? {beforeUtc: new Date(Date.parse(`${toDate}T00:00:00.000Z`) + 86400000).toISOString()} : {}),
  }), [fromDate, toDate]);

  const [controlledPage, setControlledPage] = React.useState(
    Number(restoredControlledFilters.page) || 1
  );
  const [controlledPageSize] = React.useState(20);
  const [controlledStatus, setControlledStatus] = React.useState(
    CONTROLLED_STATUS_OPTIONS.includes(restoredControlledFilters.status)
      ? restoredControlledFilters.status
      : "All"
  );
  const [controlledPurpose, setControlledPurpose] = React.useState(
    CONTROLLED_PURPOSE_OPTIONS.includes(restoredControlledFilters.purpose)
      ? restoredControlledFilters.purpose
      : "All"
  );
  const [controlledLoading, setControlledLoading] = React.useState(false);
  const [controlledItems, setControlledItems] = React.useState([]);
  const [controlledTotal, setControlledTotal] = React.useState(0);
  const [controlledError, setControlledError] = React.useState(null);
  const request = useAiRequestGuard({usage: [page, status, source, range], controlled: [controlledPage, controlledStatus, controlledPurpose, range]});
  const changeRange = (name, value) => {
    const nextFrom = name === "from" ? value : fromDate, nextTo = name === "to" ? value : toDate;
    setFromDate(nextFrom); setToDate(nextTo); setPage(1); setControlledPage(1);
    writeAiAgentDraft(USAGE_FILTER_DRAFT_KEY, {page: 1, status, source, fromDate: nextFrom, toDate: nextTo}, scope);
  };

  const updateUsageFilters = updater => {
    const next =
      typeof updater === "function" ? updater({ page, status, source }) : updater;
    const nextState = {
      page: Number(next.page) || 1,
      status: STATUS_OPTIONS.includes(next.status) ? next.status : "All",
      source: typeof next.source === "string" && next.source.length <= 32 ? next.source : "All",
      fromDate, toDate,
    };
    setPage(nextState.page);
    setStatus(nextState.status);
    setSource(nextState.source);
    writeAiAgentDraft(USAGE_FILTER_DRAFT_KEY, nextState, scope);
  };

  const updateControlledFilters = updater => {
    const next =
      typeof updater === "function"
        ? updater({ page: controlledPage, status: controlledStatus, purpose: controlledPurpose })
        : updater;
    const nextState = {
      page: Number(next.page) || 1,
      status: CONTROLLED_STATUS_OPTIONS.includes(next.status)
        ? next.status
        : "All",
      purpose: CONTROLLED_PURPOSE_OPTIONS.includes(next.purpose)
        ? next.purpose
        : "All",
    };
    setControlledPage(nextState.page);
    setControlledStatus(nextState.status);
    setControlledPurpose(nextState.purpose);
    writeAiAgentDraft(CONTROLLED_FILTER_DRAFT_KEY, nextState, scope);
  };

  const loadUsage = React.useCallback(async () => {
    const current = request("usage");
    setLoading(true); setLoadError(""); setItems([]); setTotal(0); setSummary(null);
    try {
      const result = await getAiAgentUsage({
        ...range,
        page,
        pageSize,
        status: status === "All" ? undefined : status,
        source: source === "All" ? undefined : source,
      });
      if (!current()) return;
      setItems(result.items); setTotal(result.total); setSummary(result.summary || null);
      setSources([...new Set([...SOURCE_OPTIONS, ...(result.sources || []), source])]);
    } catch (error) {
      if (!current()) return;
      const message =
        error?.response?.data?.message || "Failed to load AI usage.";
      setLoadError(message); toast.error(message);
    } finally {
      if (current()) setLoading(false);
    }
  }, [page, pageSize, source, status, range, request]);

  const loadControlledAnswerSends = React.useCallback(async () => {
    const current = request("controlled");
    setControlledItems([]); setControlledTotal(0); setControlledLoading(true);
    setControlledError(null);
    try {
      const result = await getControlledAnswerSendLogs({
        ...range,
        page: controlledPage,
        pageSize: controlledPageSize,
        status:
          controlledStatus === "All" ? undefined : controlledStatus,
        purpose:
          controlledPurpose === "All" ? undefined : controlledPurpose,
      });
      if (!current()) return;
      setControlledItems(result.items);
      setControlledTotal(result.total);
    } catch (error) {
      if (!current()) return;
      const message =
        error?.response?.data?.message ||
        "Failed to load controlled answer sends.";
      setControlledItems([]);
      setControlledTotal(0);
      setControlledError(message);
      toast.error(message);
    } finally {
      if (current()) setControlledLoading(false);
    }
  }, [controlledPage, controlledPageSize, controlledPurpose, controlledStatus, range, request]);

  React.useEffect(() => {
    loadUsage();
  }, [loadUsage]);

  React.useEffect(() => {
    if (activeTab === CONTROLLED_SENDS_TAB) {
      loadControlledAnswerSends();
    }
  }, [activeTab, loadControlledAnswerSends]);

  const loadQuotas = React.useCallback(async () => {
    const current = request("quota"); setQuotaLoading(true); setQuotaError(""); setQuota(null);
    try { const result = await getAiAgentQuotas(); if (current()) setQuota(result); }
    catch (error) { if (current()) setQuotaError(error?.response?.data?.message || "AI quota availability could not be verified."); }
    finally { if (current()) setQuotaLoading(false); }
  }, [request]);
  React.useEffect(() => { loadQuotas(); }, [loadQuotas]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const controlledTotalPages = Math.max(
    1,
    Math.ceil(controlledTotal / controlledPageSize)
  );

  return (
    <div className="p-6 bg-slate-50 min-h-[calc(100vh-80px)]">
      <div className="max-w-6xl mx-auto space-y-4">
        <header className="flex items-start justify-between gap-4 py-1">
          <div><h1 className="flex items-center gap-2 text-lg font-semibold text-slate-900"><Activity aria-hidden="true" className="h-5 w-5 text-emerald-700" />Usage &amp; activity</h1><p className="mt-1 text-sm text-slate-600">Review AI activity, sending records and saved business outcomes.</p></div>
          {canViewSettings ? <Link to="/app/ai-agent/settings" className="inline-flex h-9 shrink-0 items-center gap-2 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-600"><ArrowLeft aria-hidden="true" className="h-4 w-4" />Back to setup</Link> : null}
        </header>
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <section aria-label="UTC report range" className="flex flex-wrap items-end gap-3 border-b border-slate-200 bg-slate-50 px-5 py-4">
            <label className="text-sm text-slate-700">From date (UTC)<input type="date" value={fromDate} onChange={event => changeRange("from", event.target.value)} className="mt-1 block h-9 rounded-md border border-slate-300 bg-white px-2 text-sm" /></label>
            <label className="text-sm text-slate-700">Through date (UTC)<input type="date" value={toDate} onChange={event => changeRange("to", event.target.value)} className="mt-1 block h-9 rounded-md border border-slate-300 bg-white px-2 text-sm" /></label>
            <details className="ml-auto text-xs text-slate-600"><summary className="cursor-pointer"><Info aria-hidden="true" className="mr-1 inline h-3.5 w-3.5" />About the date range</summary><p className="mt-2 max-w-sm">Whole UTC days, through the selected final day. Clear both dates for all time. Dates do not change the current quota window.</p></details>
          </section>
          <section aria-label="Current AI quotas" className="border-b border-slate-200 px-5 py-3">
            <AiLoadError message={quotaError} onRetry={loadQuotas} loading={quotaLoading} label="quotas" />
            {quotaLoading ? <p role="status" className="text-xs text-slate-500">Checking quota availability...</p> : null}
            <details className="text-xs text-slate-600"><summary className="cursor-pointer">Current AI quota availability <span className="ml-2 text-slate-400">View allowance for the current window</span></summary>
              <p className="mt-2">This is the current allowance, separate from the historical report range.</p>
              {quota ? <><p className="mt-2">Checked: {quota.generatedAtUtc ? new Date(quota.generatedAtUtc).toLocaleString() : "Not recorded"}</p><ul className="mt-2 space-y-2">{(quota.items || []).map(item => <li key={`${item.quotaKey}-${item.period}`}><span className="font-medium text-slate-700">{item.quotaKey}:</span> {item.limit == null ? "Unlimited" : item.remaining == null ? "Availability not verified" : `${item.remaining} remaining of ${item.limit}`} &middot; {item.period}<p className="text-slate-500">Window starts {item.windowStartUtc || "Not recorded"}</p></li>)}</ul>{!quota.items?.length ? <p className="mt-2">AI quotas are not configured or not available in this snapshot. Availability is not verified.</p> : null}</> : null}
            </details>
          </section>
          <nav aria-label="Usage views" className="flex flex-wrap gap-6 border-b border-slate-200 bg-slate-50 px-5">
            {[{id: AI_USAGE_TAB, label: "AI usage"}, {id: CONTROLLED_SENDS_TAB, label: "Sending records"}, ...(canOperate ? [{id: OPERATIONS_TAB, label: "Outcomes & delivery"}] : [])].map(tab => <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)} aria-pressed={activeTab === tab.id}
              className={`border-b-2 py-3 text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-emerald-600 ${activeTab === tab.id ? "border-emerald-600 font-medium text-slate-900" : "border-transparent text-slate-600 hover:text-slate-900"}`}>{tab.label}</button>)}
          </nav>

          {activeTab === OPERATIONS_TAB && canOperate ? <AiActivityPanel range={range} canViewSettings={canViewSettings} /> : activeTab === AI_USAGE_TAB ? (
            <div className="p-5">
              <div className="flex flex-wrap items-end gap-3">
                <label className="text-sm text-slate-700">
                  Status
                  <select
                    className="ml-2 h-9 rounded-md border border-slate-300 bg-white px-2 text-sm"
                    value={status}
                    onChange={event => {
                      updateUsageFilters(prev => ({
                        ...prev,
                        page: 1,
                        status: event.target.value,
                      }));
                    }}
                  >
                    {STATUS_OPTIONS.map(option => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="text-sm text-slate-700">
                  Source
                  <select
                    className="ml-2 h-9 rounded-md border border-slate-300 bg-white px-2 text-sm"
                    value={source}
                    onChange={event => {
                      updateUsageFilters(prev => ({
                        ...prev,
                        page: 1,
                        source: event.target.value,
                      }));
                    }}
                  >
                    {sources.map(option => (
                      <option key={option} value={option}>
                        {SOURCE_LABELS[option] || option}
                      </option>
                    ))}
                  </select>
                </label>

                <button
                  type="button"
                  onClick={loadUsage}
                  aria-label="Refresh" title="Refresh" className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-300 text-slate-600 hover:bg-slate-50"
                >
                  <RefreshCcw className="h-4 w-4" />
                </button>
              </div>

              <AiLoadError message={loadError} onRetry={loadUsage} loading={loading} label="usage" />
              {summary ? <section aria-label="Selected usage totals" className="mt-4 rounded-md border border-slate-200 text-sm">
                <div className="grid divide-y divide-slate-200 md:grid-cols-3 md:divide-x md:divide-y-0">
                  <div className="p-3"><p className="text-xs text-slate-500">AI activity records</p><p className="mt-1 text-lg font-semibold text-slate-900">{summary.totalRecords}</p><p className="mt-1 text-xs text-slate-600">Completed activity: {summary.statusCounts?.Completed || 0} &middot; Denied: {summary.statusCounts?.Denied || 0} &middot; Failed: {summary.statusCounts?.Failed || 0} &middot; Started: {summary.statusCounts?.Started || 0}</p></div>
                  <div className="p-3"><p className="text-xs text-slate-500">Recorded tokens</p><p className="mt-1 text-lg font-semibold text-slate-900">{summary.recordedTokens ?? "Not recorded"}</p><p className="mt-1 text-xs text-slate-600">{summary.tokensRecorded == null ? "Coverage not reported" : `${summary.tokensRecorded}/${summary.totalRecords} records`}</p></div>
                  <div className="p-3"><p className="text-xs text-slate-500">Estimated AI cost (USD)</p><p className="mt-1 text-lg font-semibold text-slate-900">{money(summary.recordedEstimateUsd)}</p><p className="mt-1 text-xs text-slate-600">{summary.costsRecorded == null ? "Coverage not reported" : `${summary.costsRecorded}/${summary.totalRecords} records`}</p></div>
                </div>
                <details className="border-t border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600"><summary className="cursor-pointer">What these totals include</summary><p className="mt-2">{summary.costBasis} Actual customer billing, WhatsApp fees and platform charges are not measured by this amount. No currencies are combined. Completed AI activity does not confirm a sent message or a saved enquiry.</p></details>
              </section> : null}
              <div className="mt-4 overflow-x-auto rounded-md border border-slate-200">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr className="text-left border-b border-slate-200 text-slate-600">
                      <th className="px-3 py-2 font-medium">Time</th>
                      <th className="px-3 py-2 font-medium">Status</th>
                      <th className="px-3 py-2 font-medium">Source</th>
                      <th className="px-3 py-2 font-medium">Provider/Model</th>
                      <th className="px-3 py-2 font-medium">Tokens</th>
                      <th className="px-3 py-2 font-medium">Cost USD</th>
                      <th className="px-3 py-2 font-medium">Denied/Error</th>
                      <th className="px-3 py-2 font-medium">Response Preview</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={8} className="py-6 text-slate-500">
                          Loading usage records...
                        </td>
                      </tr>
                    ) : items.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="py-6 text-slate-500">
                          {loadError ? "Usage is unavailable. Retry the load." : "No usage records found."}
                        </td>
                      </tr>
                    ) : (
                      items.map(item => (
                        <tr key={item.id} className="border-b border-slate-100 align-top">
                          <td className="whitespace-nowrap px-3 py-2">
                            {item.createdAtUtc
                              ? new Date(item.createdAtUtc).toLocaleString()
                              : "-"}
                          </td>
                          <td className="px-3 py-2">{item.status || "-"}</td>
                          <td className="px-3 py-2">{SOURCE_LABELS[item.source] || item.source || "-"}</td>
                          <td className="px-3 py-2">
                            {item.provider || "-"} / {item.model || "-"}
                          </td>
                          <td className="px-3 py-2">
                            {item.totalTokens ?? (item.inputTokens != null && item.outputTokens != null ? item.inputTokens + item.outputTokens : "Not recorded")}
                          </td>
                          <td className="px-3 py-2">
                            {item.estimatedCostUsd == null ? "Not recorded" : Number(item.estimatedCostUsd).toFixed(6)}
                          </td>
                          <td className="px-3 py-2">
                            {item.deniedReason || (item.errorCode === "PROVIDER_ERROR" ? "Provider error" : item.errorCode) || "-"}
                            {item.deniedReason || item.errorCode ? <details className="mt-1 text-xs text-slate-500"><summary className="cursor-pointer">What to check</summary><p className="mt-2 max-w-xs">{item.errorCode === "PROVIDER_ERROR" ? "The AI provider reported an error. Ask an administrator to check the provider connection, configuration and availability." : activityExplanation(item.errorCode || item.deniedReason)}</p><p className="mt-1 break-all">Recorded reason: {item.deniedReason || item.errorCode}</p></details> : null}
                          </td>
                          <td className="max-w-[320px] px-3 py-2">
                            <span className="line-clamp-2 text-slate-600">
                              {item.responsePreview || "-"}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 flex items-center justify-between text-sm text-slate-600">
                <div>
                  Page {page} of {totalPages} ({total} records)
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() =>
                      updateUsageFilters(prev => ({
                        ...prev,
                        page: Math.max(1, prev.page - 1),
                      }))
                    }
                    className={`rounded border px-3 py-1 ${
                      page <= 1
                        ? "border-slate-200 text-slate-400 cursor-not-allowed"
                        : "border-slate-300 hover:bg-slate-50"
                    }`}
                  >
                    Prev
                  </button>
                  <button
                    type="button"
                    disabled={page >= totalPages}
                    onClick={() =>
                      updateUsageFilters(prev => ({
                        ...prev,
                        page: Math.min(totalPages, prev.page + 1),
                      }))
                    }
                    className={`rounded border px-3 py-1 ${
                      page >= totalPages
                        ? "border-slate-200 text-slate-400 cursor-not-allowed"
                        : "border-slate-300 hover:bg-slate-50"
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-5">
              <div className="flex flex-wrap items-end gap-3">
                <label className="text-sm text-slate-700">
                  Status
                  <select
                    className="ml-2 h-9 rounded-md border border-slate-300 bg-white px-2 text-sm"
                    value={controlledStatus}
                    onChange={event => {
                      updateControlledFilters(prev => ({
                        ...prev,
                        page: 1,
                        status: event.target.value,
                      }));
                    }}
                  >
                    {CONTROLLED_STATUS_OPTIONS.map(option => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="text-sm text-slate-700">
                  Purpose
                  <select
                    className="ml-2 h-9 rounded-md border border-slate-300 bg-white px-2 text-sm"
                    value={controlledPurpose}
                    onChange={event => {
                      updateControlledFilters(prev => ({
                        ...prev,
                        page: 1,
                        purpose: event.target.value,
                      }));
                    }}
                  >
                    {CONTROLLED_PURPOSE_OPTIONS.map(option => (
                      <option key={option} value={option}>
                        {PURPOSE_LABELS[option] || option}
                      </option>
                    ))}
                  </select>
                </label>

                <button
                  type="button"
                  onClick={loadControlledAnswerSends}
                  aria-label="Refresh" title="Refresh" className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-300 text-slate-600 hover:bg-slate-50"
                >
                  <RefreshCcw className="h-4 w-4" />
                </button>
              </div>

              {controlledError ? (
                <div className="mt-4 flex items-center justify-between rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  <span>{controlledError}</span>
                  <button
                    type="button"
                    onClick={loadControlledAnswerSends}
                    className="rounded border border-rose-200 bg-white px-3 py-1 text-rose-700 hover:bg-rose-50"
                  >
                    Retry
                  </button>
                </div>
              ) : null}

              <p className="mt-4 flex items-start gap-2 text-xs text-slate-500"><Info aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />These records track send attempts. A successful attempt does not confirm a customer delivery receipt.</p>
              <div className="mt-4 overflow-x-auto rounded-md border border-slate-200">
                <table className="min-w-full text-sm"><thead className="bg-slate-50 text-slate-600"><tr>{["Time", "Status", "Purpose", "Configured reply", "Attempts", "Evidence"].map(label => <th key={label} className="px-3 py-2 text-left font-medium">{label}</th>)}</tr></thead><tbody className="divide-y divide-slate-200">
                  {controlledLoading ? <tr><td colSpan={6} className="px-3 py-6 text-slate-500">Loading controlled answer sends...</td></tr> : !controlledItems.length ? <tr><td colSpan={6} className="px-3 py-6 text-slate-500">{controlledError ? "Controlled sends are unavailable. Retry the load." : "No controlled answer sends found yet."}</td></tr> : controlledItems.map(item => <tr key={item.id} className="align-top text-slate-700">
                    <td className="whitespace-nowrap px-3 py-3">{item.createdAtUtc ? new Date(item.createdAtUtc).toLocaleString() : "-"}</td>
                    <td className="px-3 py-3"><span className={`inline-flex rounded-md px-2 py-1 text-xs ${STATUS_BADGE_CLASSES[item.status] || "bg-slate-100 text-slate-700"}`}>{item.status || "-"}</span></td>
                    <td className="px-3 py-3">{PURPOSE_LABELS[item.purpose] || item.purpose || "-"}</td>
                    <td className="max-w-xs px-3 py-3">{item.controlledAnswerCardTitle || "-"}{item.semanticCategory ? <p className="mt-1 text-xs text-slate-500">{item.semanticCategory}</p> : null}</td>
                    <td className="px-3 py-3">{item.attemptCount ?? 0}</td>
                    <td className="max-w-xs px-3 py-3 text-xs text-slate-600">{item.sendErrorCode || item.sendErrorMessage ? <p className="mb-2 break-words text-rose-700">{item.sendErrorCode || item.sendErrorMessage}</p> : null}<details><summary className="cursor-pointer">Sending details</summary><dl className="mt-2 space-y-2 break-all">{[["Message order", item.messageOrder ?? "-"], ["Reply mode", item.replyMode || "-"], ["Card", item.controlledAnswerCardId || "-"], ["Inbound message log", item.inboundMessageLogId || "-"], ["Outbound message log", item.outboundMessageLogId || "-"], ["Error message", item.sendErrorMessage || "-"]].map(([label,value]) => <div key={label}><dt className="text-slate-500">{label}</dt><dd>{value}</dd></div>)}</dl></details></td>
                  </tr>)}
                </tbody></table>
              </div>

              <div className="mt-4 flex items-center justify-between text-sm text-slate-600">
                <div>
                  Page {controlledPage} of {controlledTotalPages} (
                  {controlledTotal} records)
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={controlledPage <= 1}
                    onClick={() =>
                      updateControlledFilters(prev => ({
                        ...prev,
                        page: Math.max(1, prev.page - 1),
                      }))
                    }
                    className={`rounded border px-3 py-1 ${
                      controlledPage <= 1
                        ? "border-slate-200 text-slate-400 cursor-not-allowed"
                        : "border-slate-300 hover:bg-slate-50"
                    }`}
                  >
                    Prev
                  </button>
                  <button
                    type="button"
                    disabled={controlledPage >= controlledTotalPages}
                    onClick={() =>
                      updateControlledFilters(prev => ({
                        ...prev,
                        page: Math.min(controlledTotalPages, prev.page + 1),
                      }))
                    }
                    className={`rounded border px-3 py-1 ${
                      controlledPage >= controlledTotalPages
                        ? "border-slate-200 text-slate-400 cursor-not-allowed"
                        : "border-slate-300 hover:bg-slate-50"
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default withAiAgentScope(AiAgentUsagePage);
